import React, { useState, useEffect, useRef } from 'react';
import { 
  MdCloudUpload, 
  MdDescription, 
  MdPhoto, 
  MdVideoLibrary,
  MdDelete,
  MdVisibility,
  MdLink,
  MdContentCopy
} from 'react-icons/md';
import { uploadMedia, deleteUserMedia } from '../../../api/media.service';
import { getCookie } from '../../../api/cookie';
import { makeAuthenticatedFetch } from '../../../api/whatsapp.service';
import LoadingComponent from '../../../components/LoadingComponent';

interface UploadedFile {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document';
  size: number;
  url: string;
  uploadDate: string;
  fileHandle?: string;
  s3Key?: string;
  isUploading?: boolean;
  uploadProgress?: number;
  imageLoading?: boolean;
}

export default function FileUpload() {
  
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debug: Log component state
  console.log('FileUpload state:', { files, isLoading, isUploading });

  // Load existing files on component mount
  useEffect(() => {
    console.log('FileUpload useEffect triggered.');
    const currentPhoneNumberId = sessionStorage.getItem('phoneNumberId');
    console.log('PhoneNumberId on mount:', currentPhoneNumberId);
    
    if (!currentPhoneNumberId) {
      console.warn('No phoneNumberId found in sessionStorage on component mount. Please ensure a WABA ID is selected or user is logged in.');
    }
    
    fetchUserMedia(); // Fetch from API first
  }, []);

  const loadExistingFiles = () => {
    // Clear localStorage to ensure we only show real API data
    localStorage.removeItem('uploadedFiles');
    setFiles([]); // Ensure no hardcoded data
  };

  const fetchUserMedia = async () => {
    try {
      setIsLoading(true);
      const phoneNumberId = sessionStorage.getItem('phoneNumberId');
      if (!phoneNumberId) {
        loadExistingFiles();
        return;
      }
      
      const response = await makeAuthenticatedFetch(`https://api.frontdesk24.com/api/user-media/${phoneNumberId}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User media response:', data);
        
        if (data.success && data.data && data.data.mediaItems) {
          // Transform API response to match our UploadedFile interface
          const apiFiles: UploadedFile[] = data.data.mediaItems.map((item: any) => {
            
            // Extract filename from s3Key or use mediaId
            let fileName = item.mediaId; // Default fallback
            if (item.s3Key) {
              const s3FileName = item.s3Key.split('/').pop();
              if (s3FileName) {
                // Remove timestamp prefix and extract original filename
                const parts = s3FileName.split('-');
                if (parts.length > 2) {
                  fileName = parts.slice(2).join('-').replace(/\.(png|jpg|jpeg|gif|mp4|pdf|doc|docx)$/i, '');
                } else {
                  fileName = s3FileName;
                }
              }
            }
            
            return {
              id: item.mediaId,
              name: fileName,
              type: getFileTypeFromUrl(item.mediaUrl),
              size: 0, // Size not provided in API response
              url: item.mediaUrl,
              uploadDate: item.createdAt,
              fileHandle: item.mediaId,
              s3Key: item.s3Key
            };
          });
          
          setFiles(apiFiles);
        } else {
          console.log('No media items found in API response');
          setFiles([]); // Clear files if no media items
        }
      } else {
        console.error('Failed to fetch user media:', response.status, response.statusText);
        // Don't fallback to localStorage - show empty state instead
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching user media:', error);
      // Don't fallback to localStorage - show empty state instead
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFileTypeFromUrl = (url: string): 'image' | 'video' | 'document' => {
    if (!url) return 'document';
    
    const extension = url.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return 'image';
    }
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      return 'video';
    }
    return 'document';
  };

  const saveFiles = (updatedFiles: UploadedFile[]) => {
    setFiles(updatedFiles);
    localStorage.setItem('uploadedFiles', JSON.stringify(updatedFiles));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (fileList: FileList) => {
    setIsUploading(true);
    
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        await uploadSingleFile(file);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      // You can add a toast notification or other UI feedback here instead of alert
    } finally {
      setIsUploading(false);
    }
  };

  const uploadSingleFile = async (file: File) => {
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    
    // Add file to UI immediately with uploading state
    const tempFile: UploadedFile = {
      id: tempId,
      name: file.name,
      type: getFileType(file.type),
      size: file.size,
      url: '', // Will be set after upload
      uploadDate: new Date().toISOString(),
      isUploading: true,
      uploadProgress: 0,
      imageLoading: true
    };
    
    setFiles(prevFiles => [...prevFiles, tempFile]);
    
    try {
      console.log('Attempting to upload file:', file.name);
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setFiles(prevFiles => 
          prevFiles.map(f => 
            f.id === tempId 
              ? { ...f, uploadProgress: Math.min((f.uploadProgress || 0) + Math.random() * 20, 90) }
              : f
          )
        );
      }, 200);
      
      const response = await uploadMedia(file);
      console.log('Response from uploadMedia:', response);
      
      clearInterval(progressInterval);
      
      if (response && response.success && response.mediaUrl) {
        console.log('File uploaded successfully, updating UI...');
        
        // Update the temp file with real data
        const uploadedFile: UploadedFile = {
          id: response.mediaId || Date.now().toString(),
          name: file.name,
          type: getFileType(file.type),
          size: file.size,
          url: response.mediaUrl,
          uploadDate: new Date().toISOString(),
          fileHandle: response.mediaId,
          s3Key: response.s3Key,
          isUploading: false,
          uploadProgress: 100,
          imageLoading: true // Will be set to false when image loads
        };
        
        // Replace temp file with real file
        setFiles(prevFiles => 
          prevFiles.map(f => f.id === tempId ? uploadedFile : f)
        );
        
        // Also refresh from API to ensure consistency (in background)
        setTimeout(async () => {
          console.log('Refreshing from API in background...');
          await fetchUserMedia();
        }, 500);
        
      } else {
        throw new Error(response?.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Remove the temp file on error
      setFiles(prevFiles => prevFiles.filter(f => f.id !== tempId));
      throw error;
    }
  };

  const getFileType = (mimeType: string): 'image' | 'video' | 'document' => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    return 'document';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deleteFile = async (fileId: string) => {
    try {
      const fileToDelete = files.find(file => file.id === fileId);
      if (!fileToDelete) {
        console.error('File not found for deletion');
        return;
      }

      console.log('Attempting to delete file:', fileToDelete.name);
      
      // If we have an s3Key, call the delete API
      if (fileToDelete.s3Key) {
        const deleteResponse = await deleteUserMedia(fileToDelete.s3Key);
        if (deleteResponse.success) {
          console.log('File deleted successfully from server');
        } else {
          console.error('Failed to delete file from server:', deleteResponse.error);
          // Still remove from UI even if server deletion fails
        }
      } else {
        console.log('No s3Key found, removing from UI only');
      }

      // Remove from UI
      const updatedFiles = files.filter(file => file.id !== fileId);
      setFiles(updatedFiles);
      
      // Also refresh from API to ensure consistency
      setTimeout(async () => {
        console.log('Refreshing from API after deletion...');
        await fetchUserMedia();
      }, 500);
      
    } catch (error) {
      console.error('Error deleting file:', error);
      // Still remove from UI even if there's an error
      const updatedFiles = files.filter(file => file.id !== fileId);
      setFiles(updatedFiles);
    }
  };

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(''), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <MdPhoto className="h-6 w-6 text-green-500" />;
      case 'video':
        return <MdVideoLibrary className="h-6 w-6 text-blue-500" />;
      default:
        return <MdDescription className="h-6 w-6 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'image':
        return 'green';
      case 'video':
        return 'blue';
      default:
        return 'gray';
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">
                File Upload Manager
              </h2>
              <p className="text-sm text-gray-600">
                Upload and manage media files for WhatsApp messages
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <MdCloudUpload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <h4 className="text-sm font-medium text-gray-700 mb-1">
              Drop files here or click to upload
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Images, videos, documents (Max 10MB)
            </p>
            <button
              className={`h-8 px-4 rounded-lg text-sm font-medium transition duration-200
                        ${!isUploading 
                          ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white' 
                          : 'bg-gray-400 text-gray-500 cursor-not-allowed'
                        }`}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Choose Files'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileInput}
              className="hidden"
            />
          </div>
        </div>

        {/* Files Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {isLoading ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading media files...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <MdCloudUpload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No files uploaded yet</p>
              <p className="text-gray-400 text-sm mt-2">Upload your first media file to get started</p>
            </div>
          ) : (
            files.map((file) => (
            <div key={file.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
              {/* Image Preview */}
              {file.type === 'image' ? (
                <div className="relative h-48 bg-gray-100">
                  {/* Upload Progress Overlay */}
                  {file.isUploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                      <div className="text-center text-white">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                        <div className="text-sm">Uploading...</div>
                        <div className="text-xs mt-1">{Math.round(file.uploadProgress || 0)}%</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Image Loading Skeleton */}
                  {file.imageLoading && !file.isUploading && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
                    </div>
                  )}
                  
                  {/* Actual Image */}
                  {file.url && (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      onLoad={() => {
                        setFiles(prevFiles => 
                          prevFiles.map(f => 
                            f.id === file.id ? { ...f, imageLoading: false } : f
                          )
                        );
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        setFiles(prevFiles => 
                          prevFiles.map(f => 
                            f.id === file.id ? { ...f, imageLoading: false } : f
                          )
                        );
                      }}
                    />
                  )}
                  
                  {/* Fallback Icon */}
                  <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-100">
                    <MdPhoto className="h-12 w-12 text-gray-400" />
                  </div>
                  
                  {/* Action buttons overlay - only show when not uploading */}
                  {!file.isUploading && (
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button
                        className="h-8 w-8 rounded-full flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors text-white"
                        onClick={() => {
                          setSelectedFile(file);
                          setShowPreview(true);
                          setImageLoading(true);
                          setImageError(false);
                        }}
                      >
                        <MdVisibility className="h-4 w-4" />
                      </button>
                      <button
                        className="h-8 w-8 rounded-full flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 transition-colors text-white"
                        onClick={() => {
                          setSelectedFile(file);
                          setShowUrlDialog(true);
                        }}
                      >
                        <MdLink className="h-4 w-4" />
                      </button>
                      <button
                        className="h-8 w-8 rounded-full flex items-center justify-center bg-red-500 bg-opacity-80 hover:bg-opacity-100 transition-colors text-white"
                        onClick={() => deleteFile(file.id)}
                      >
                        <MdDelete className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  {getFileIcon(file.type)}
                </div>
              )}
              
              {/* File Info */}
              <div className="p-4">
                <p className="text-sm font-semibold text-gray-800 truncate mb-2">
                  {file.name}
                </p>
                <div className="text-xs text-gray-500 mb-3">
                  {file.isUploading ? 'Uploading...' : `Uploaded: ${formatDate(file.uploadDate)}`}
                </div>
                <button
                  className={`w-full text-sm font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center ${
                    file.isUploading 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                  onClick={() => copyToClipboard(file.url)}
                  disabled={file.isUploading}
                >
                  <MdContentCopy className="h-4 w-4 mr-1" />
                  {file.isUploading ? 'Uploading...' : (copiedUrl === file.url ? 'Copied!' : 'Copy URL')}
                </button>
              </div>
            </div>
            ))
          )}
        </div>

        {files.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <MdDescription className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h5 className="text-lg font-semibold text-gray-500 mb-2">
              No files uploaded yet
            </h5>
            <p className="text-sm text-gray-400">
              Upload your first file to get started
            </p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold">{selectedFile?.name}</h3>
            </div>
            <div className="p-6">
              {selectedFile && (
                <div className="text-center">
                  {selectedFile.type === 'image' ? (
                    <div className="relative">
                      {imageLoading && (
                        <div className="flex items-center justify-center h-96">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                      {imageError ? (
                        <div className="p-8 bg-gray-100 rounded-lg">
                          <MdPhoto className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">Failed to load image preview</p>
                          <p className="text-sm text-gray-500 mt-2">URL: {selectedFile.url}</p>
                        </div>
                      ) : (
                        <img
                          src={selectedFile.url}
                          alt={selectedFile.name}
                          className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                          onLoad={() => {
                            setImageLoading(false);
                            setImageError(false);
                          }}
                          onError={() => {
                            setImageLoading(false);
                            setImageError(true);
                            console.error('Image failed to load:', selectedFile.url);
                          }}
                        />
                      )}
                      <div className="mt-4 text-sm text-gray-600">
                        <p><strong>File:</strong> {selectedFile.name}</p>
                        <p><strong>Size:</strong> {formatFileSize(selectedFile.size)}</p>
                        <p><strong>Uploaded:</strong> {formatDate(selectedFile.uploadDate)}</p>
                      </div>
                    </div>
                  ) : selectedFile.type === 'video' ? (
                    <video
                      src={selectedFile.url}
                      controls
                      className="max-w-full max-h-96 mx-auto rounded-lg"
                    />
                  ) : (
                    <div className="p-8 bg-gray-100 rounded-lg">
                      <MdDescription className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">
                        Document preview not available
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end space-x-2">
              <button
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50"
                onClick={() => selectedFile && copyToClipboard(selectedFile.url)}
              >
                Copy URL
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Modal */}
      {showUrlDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold">File URL</h3>
            </div>
            <div className="p-6">
              {selectedFile && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      File Name: {selectedFile.name}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      Type: {selectedFile.type.toUpperCase()}
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      Size: {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      URL:
                    </p>
                    <div className="flex items-center space-x-2">
                      <input
                        value={selectedFile.url}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <button
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        onClick={() => copyToClipboard(selectedFile.url)}
                      >
                        {copiedUrl === selectedFile.url ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Usage:</strong> Copy this URL and paste it in your WhatsApp message templates or utility messages to include this file.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end">
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                onClick={() => setShowUrlDialog(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
